import { TFoodGroup } from '@mindfulchefuk/config/mealPlanConfig'
import {
  RecipeBadge,
  TAPITag,
  TRecipeClaim,
} from '@mindfulchefuk/features/Recipes/interfaces'
import {
  TAPIExpandedAvailabilityRecipe,
  TAPIRecipeAvailability,
  TAPIRecipeAvailabilityMacro,
  TAPISummaryRecipeAvailability,
  TCarbonRating,
  TExpandedAvailabilityRecipe,
  TRecipeAvailability,
  TRecipeAvailabilityMacros,
  TSummaryAvailabilityRecipe,
  TAPIRecipeAvailabilitiesMacros,
} from '@mindfulchefuk/types/recipeAvailabilities'

const transformMacro = (
  macro: TAPIRecipeAvailabilityMacro
): TRecipeAvailabilityMacros => {
  const keys = Object.keys(macro)

  if (keys.length < 1) {
    return {}
  }

  return {
    calories: macro.calories,
    carbohydrates: macro.carbohydrates,
    fat: macro.fat,
    fibre: macro.fibre,
    kilojoules: macro.kilojoules,
    protein: macro.protein,
    salt: macro.salt,
    saturatedFat: macro.saturated_fat,
    sugars: macro.sugars,
  }
}

const transformMacros = (
  macros: TAPIRecipeAvailabilitiesMacros,
  portionCount: number
) => {
  const onePortion = transformMacro(macros['1_portion'])
  const twoPortion = transformMacro(macros['2_portion'])
  const fourPortion = transformMacro(macros['4_portion'])

  let calories
  let carbs

  if (portionCount === 1) {
    calories = onePortion.calories
    carbs = onePortion.carbohydrates
  } else if (portionCount === 2) {
    calories = twoPortion.calories
    carbs = twoPortion.carbohydrates
  } else {
    calories = fourPortion.calories
    carbs = fourPortion.carbohydrates
  }

  return {
    '1Portion': onePortion,
    '2Portion': twoPortion,
    '4Portion': fourPortion,
    calories,
    carbs,
  }
}

const transformTags = (tags: TAPITag[]): TRecipeClaim[] => {
  const mapped = tags.map((tag: TAPITag) => {
    return {
      backgroundColor: tag.background_colour ?? '#FFF',
      description: tag.description,
      name: tag.name,
      sortOrder: tag.sort_order ?? 0,
      textColor: tag.text_colour ?? '#000',
    }
  })

  // The top three tags from the api should be displayed in ascending value sort order
  const top3 = mapped.slice(0, 3).sort((a, b) => a.sortOrder - b.sortOrder)
  const rest = mapped.slice(3, mapped.length)

  return [...top3, ...rest]
}

export const transformRecipeAvailbilityExpandedData = (
  recipeAvailability: TAPIRecipeAvailability<TAPIExpandedAvailabilityRecipe>
): TRecipeAvailability<TExpandedAvailabilityRecipe> => {
  return {
    availabilityId: recipeAvailability.id,
    portions: recipeAvailability.portions,
    price: recipeAvailability.price,
    pricePerPerson: (
      parseFloat(recipeAvailability.price) / recipeAvailability.portions
    ).toString(),
    recipe: {
      allergens: recipeAvailability.recipe.allergens,
      beforeYouStart: recipeAvailability.recipe.before_you_start,
      badges: recipeAvailability.recipe.badges as RecipeBadge[],
      carbonRating: recipeAvailability.recipe.carbon_rating as TCarbonRating,
      cookingInstructions: recipeAvailability.recipe.steps,
      cookingTime: recipeAvailability.recipe.recipe_duration,
      description: recipeAvailability.recipe.description,
      feedback: recipeAvailability.recipe.feedback,
      foodGroup: recipeAvailability.recipe.food_group as TFoodGroup,
      id: recipeAvailability.recipe.id,
      image: recipeAvailability.recipe.image,
      keyIngredientDescription:
        recipeAvailability.recipe.key_ingredient_description,
      macrosPer100g: {
        '1Portion': transformMacro(
          recipeAvailability.recipe.macros_per_100g['1_portion']
        ),
        '2Portion': transformMacro(
          recipeAvailability.recipe.macros_per_100g['2_portion']
        ),
        '4Portion': transformMacro(
          recipeAvailability.recipe.macros_per_100g['4_portion']
        ),
      },
      macrosPerPerson: {
        '1Portion': transformMacro(
          recipeAvailability.recipe.macros_per_person['1_portion']
        ),
        '2Portion': transformMacro(
          recipeAvailability.recipe.macros_per_person['2_portion']
        ),
        '4Portion': transformMacro(
          recipeAvailability.recipe.macros_per_person['4_portion']
        ),
      },
      possibleAllergens: recipeAvailability.recipe.possible_allergens,
      prepTime: recipeAvailability.recipe.recipe_prep_time,
      recipeComponents: recipeAvailability.recipe.recipe_portion_components.map(
        (component) => ({
          allergens: component.present_allergens,
          id: component.id,
          possibleAllergens: component.possible_allergens,
          title: component.display_title,
        })
      ),
      spicinessLevel: recipeAvailability.recipe.spiciness_level,
      tags: transformTags(recipeAvailability.recipe.tags),
      title: recipeAvailability.recipe.title,
    },
    sortOrder: recipeAvailability.sort_order,
  }
}

export const transformRecipeAvailabilitiesSummaryData = (recipes: {
  recipe_availabilities: TAPIRecipeAvailability<TAPISummaryRecipeAvailability>[]
}): TRecipeAvailability<TSummaryAvailabilityRecipe>[] => {
  return recipes.recipe_availabilities.map((recipeAvailability) => {
    const perPersonMacros = transformMacros(
      recipeAvailability.recipe.macros_per_person,
      recipeAvailability.portions
    )
    return {
      availabilityId: recipeAvailability.id,
      portions: recipeAvailability.portions,
      price: recipeAvailability.price,
      pricePerPerson: (
        parseFloat(recipeAvailability.price) / recipeAvailability.portions
      ).toString(),
      recipe: {
        badges: recipeAvailability.recipe.badges as RecipeBadge[],
        cookingTime: recipeAvailability.recipe.recipe_duration,
        feedback: recipeAvailability.recipe.feedback,
        foodGroup: recipeAvailability.recipe.food_group as TFoodGroup,
        id: recipeAvailability.recipe.id,
        image: recipeAvailability.recipe.image,
        keyIngredientDescription:
          recipeAvailability.recipe.key_ingredient_description,
        macrosPer100g: transformMacros(
          recipeAvailability.recipe.macros_per_100g,
          recipeAvailability.portions
        ),
        macrosPerPerson: perPersonMacros,
        spicinessLevel: recipeAvailability.recipe.spiciness_level,
        tags: transformTags(recipeAvailability.recipe.tags),
        title: recipeAvailability.recipe.title,
      },
      sortOrder: recipeAvailability.sort_order,
      recipeSortingOptions: {
        cookingTime: recipeAvailability.recipe.recipe_duration,
        price: parseFloat(recipeAvailability.price),
        sortOrder: recipeAvailability.sort_order,
        calories: parseFloat(perPersonMacros.calories),
        carbohydrates: parseFloat(perPersonMacros.carbs),
      },
    }
  })
}
